/**
 * Månadsvis nettokostnad — visar hur elkostnaden fördelas över året
 * utan och med batteri som två linjer. Mer läsbart än grupperade staplar
 * på smal mobil, och visar tydligt under vilka månader batteriet jobbar.
 */

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import type { HourPoint, ResultHour, Scenario } from "../sim/types";
import { importPricePerKWh, exportRevenuePerKWh } from "../sim/costs";

interface MonthlyCostRow {
  monthKey: string;
  monthLabel: string;
  utan: number;
  med: number;
}

const MONTH_NAMES_SV = [
  "jan", "feb", "mar", "apr", "maj", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];

function computeMonthlyCosts(
  baselineHours: HourPoint[],
  batteryHours: ResultHour[],
  scenario: Scenario,
): MonthlyCostRow[] {
  const baselineAnnual = baselineHours.reduce(
    (s, h) => s + h.importKWh,
    0,
  );

  const byMonth = new Map<
    string,
    { utan: number; med: number; label: string }
  >();

  for (const h of baselineHours) {
    const date = new Date(h.from);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const label = MONTH_NAMES_SV[date.getMonth()] ?? "?";
    const importPrice = importPricePerKWh(
      h,
      scenario.tax,
      scenario.supplier,
      scenario.grid,
      baselineAnnual,
    );
    const exportRev = exportRevenuePerKWh(
      h,
      scenario.tax,
      scenario.supplier,
      scenario.grid,
    );
    const cost = importPrice * h.importKWh - exportRev * h.exportKWh;
    const existing = byMonth.get(monthKey) ?? {
      utan: 0,
      med: 0,
      label,
    };
    existing.utan += cost;
    byMonth.set(monthKey, existing);
  }

  for (const h of batteryHours) {
    const date = new Date(h.from);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const existing = byMonth.get(monthKey);
    if (existing) {
      existing.med += h.netCostSek;
    }
  }

  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthKey, v]) => ({
      monthKey,
      monthLabel: v.label,
      utan: Math.round(v.utan),
      med: Math.round(v.med),
    }));
}

function CustomTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: Array<{ value: number; dataKey: string; color: string }>;
  label?: string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const utan = payload.find((p) => p.dataKey === "utan")?.value ?? 0;
  const med = payload.find((p) => p.dataKey === "med")?.value ?? 0;
  const diff = utan - med;
  return (
    <div className="chart-tooltip">
      <div className="chart-tooltip-label">{label}</div>
      <div className="chart-tooltip-row">
        <span>Utan batteri</span>
        <strong>{utan.toLocaleString("sv-SE")} kr</strong>
      </div>
      <div className="chart-tooltip-row">
        <span>Med batteri</span>
        <strong>{med.toLocaleString("sv-SE")} kr</strong>
      </div>
      <div className="chart-tooltip-row chart-tooltip-diff">
        <span>Besparing</span>
        <strong>
          {diff >= 0 ? "+" : ""}
          {diff.toLocaleString("sv-SE")} kr
        </strong>
      </div>
    </div>
  );
}

interface MonthlyCostChartProps {
  baselineHours: HourPoint[];
  batteryHours: ResultHour[];
  scenario: Scenario;
}

export default function MonthlyCostChart({
  baselineHours,
  batteryHours,
  scenario,
}: MonthlyCostChartProps) {
  const data = computeMonthlyCosts(baselineHours, batteryHours, scenario);
  if (data.length === 0) {
    return <p className="hint">Ingen data att visa.</p>;
  }

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 720;
  const height = isMobile ? 240 : 320;

  return (
    <div className="monthly-cost-chart">
      <ResponsiveContainer width="100%" height={height}>
        <LineChart
          data={data}
          margin={{ top: 12, right: 8, bottom: 8, left: 0 }}
        >
          <CartesianGrid
            stroke="var(--rule)"
            strokeDasharray="3 3"
            vertical={false}
          />
          <XAxis
            dataKey="monthLabel"
            stroke="var(--muted)"
            fontSize={isMobile ? 10 : 12}
            tickLine={false}
            axisLine={{ stroke: "var(--rule)" }}
          />
          <YAxis
            stroke="var(--muted)"
            fontSize={isMobile ? 10 : 12}
            tickLine={false}
            axisLine={false}
            tickCount={isMobile ? 4 : 5}
            tickFormatter={(v: number) => `${Math.round(v / 100) / 10}k`}
            label={{
              value: "kr",
              position: "insideTopLeft",
              fontSize: isMobile ? 10 : 11,
              fill: "var(--muted)",
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            iconType="line"
            wrapperStyle={{ fontSize: isMobile ? "0.78rem" : "0.85rem" }}
          />
          <Line
            type="monotone"
            dataKey="utan"
            name="Utan batteri"
            stroke="#7dc21d"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey="med"
            name="Med batteri"
            stroke="#058446"
            strokeWidth={2}
            dot={{ r: 3 }}
            activeDot={{ r: 5 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
